import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, Inbox, Lock, RefreshCw } from "lucide-react";
import Seo from "../components/Seo";
import { API } from "../lib/site";

const PhotoThumb = ({ photoId, adminKey }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    axios
      .get(`${API}/admin/photos/${photoId}`, {
        headers: { "X-Admin-Key": adminKey },
        responseType: "blob",
      })
      .then((r) => {
        obj = URL.createObjectURL(r.data);
        setUrl(obj);
      })
      .catch(() => setUrl(null));
    return () => obj && URL.revokeObjectURL(obj);
  }, [photoId, adminKey]);
  if (!url) return <div className="h-24 w-24 animate-pulse bg-white/10" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`admin-photo-${photoId}`}>
      <img src={url} alt="Submission upload" className="h-24 w-24 border border-white/15 object-cover" />
    </a>
  );
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("ves_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [subs, setSubs] = useState([]);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async (k) => {
    try {
      const r = await axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Key": k } });
      setSubs(r.data.submissions);
      setAuthed(true);
      setError("");
      localStorage.setItem("ves_admin_key", k);
    } catch {
      setError("Invalid access key.");
      setAuthed(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4" data-testid="admin-login">
        <Seo title="Admin | Velocity Electrical Surplus" description="Restricted area." />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(key);
          }}
          className="w-full max-w-sm border border-white/10 bg-ves-charcoal/50 p-8"
        >
          <Lock className="h-8 w-8 text-ves-green" />
          <h1 className="mt-4 font-heading text-2xl font-black text-white">SUBMISSIONS DASHBOARD</h1>
          <p className="mt-2 text-sm text-white/60">Enter the admin access key to view inquiries.</p>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="mt-5 w-full border border-white/15 bg-white/5 px-4 py-3 text-white focus:border-ves-green focus:outline-none focus:ring-2 focus:ring-ves-green/40"
            placeholder="Access key"
            data-testid="admin-key-input"
          />
          {error && <p className="mt-2 text-sm text-red-400" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            className="mt-4 w-full bg-ves-green px-5 py-3 font-heading text-sm font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
            data-testid="admin-login-button"
          >
            VIEW SUBMISSIONS
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <Seo title="Submissions | Velocity Electrical Surplus Admin" description="Restricted area." />
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-3 font-heading text-2xl font-black text-white">
          <Inbox className="h-7 w-7 text-ves-green" /> SUBMISSIONS ({subs.length})
        </h1>
        <button
          onClick={() => load(key)}
          className="flex items-center gap-2 border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:border-ves-green hover:text-ves-green"
          data-testid="admin-refresh-button"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {subs.length === 0 && (
        <p className="mt-8 border border-white/10 bg-ves-charcoal/50 p-6 text-sm text-white/60" data-testid="admin-empty">
          No submissions yet.
        </p>
      )}

      <div className="mt-6 space-y-3">
        {subs.map((s) => (
          <div key={s.id} className="border border-white/10 bg-ves-charcoal/50" data-testid={`admin-submission-${s.id}`}>
            <button
              onClick={() => setOpenId(openId === s.id ? null : s.id)}
              className="flex w-full items-center justify-between gap-4 p-4 text-left"
              data-testid={`admin-toggle-${s.id}`}
            >
              <div>
                <span className={`mr-3 inline-block px-2 py-0.5 font-heading text-[10px] font-black tracking-widest ${s.type === "inquiry" ? "bg-ves-green text-ves-black" : "border border-ves-green text-ves-green"}`}>
                  {s.type === "inquiry" ? "OFFER REQUEST" : "CONTACT"}
                </span>
                <span className="font-semibold text-white">
                  {s.fields["Contact Name"] || s.fields["Name"] || "—"}
                </span>
                <span className="ml-3 text-xs text-white/50">
                  {new Date(s.created_at).toLocaleString()} • {s.photo_count} photo(s)
                </span>
              </div>
              {openId === s.id ? <ChevronUp className="h-5 w-5 text-ves-green" /> : <ChevronDown className="h-5 w-5 text-white/50" />}
            </button>
            {openId === s.id && (
              <div className="border-t border-white/10 p-4">
                <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                  {Object.entries(s.fields).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs font-bold tracking-wide text-ves-green">{k}</dt>
                      <dd className="whitespace-pre-wrap text-sm text-white/80">{v}</dd>
                    </div>
                  ))}
                </dl>
                {s.photo_ids?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {s.photo_ids.map((pid) => (
                      <PhotoThumb key={pid} photoId={pid} adminKey={key} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
